/**
 * JSONC — JSON with comments and trailing commas, the dialect .NET accepts in
 * `appsettings*.json` and VS Code in its settings.
 *
 * `ss upgrade` used to `JSON.parse` appsettings.json bare: a file carrying one
 * `// comment` (valid for .NET) crashed the upgrade AFTER the csproj files had
 * already been rewritten — a half-upgraded project. Every config read of the
 * upgrade goes through `parseJsonc`, which never throws and reports the
 * position of a real syntax error (line/column of the ORIGINAL text: comments
 * are blanked, not removed, so offsets survive).
 *
 * The stripper is string-aware for comments AND for trailing commas (the
 * historical regex `,\s*([\]}])` also rewrote `", ]"` inside a string value).
 */

/**
 * Blank comments and trailing commas with spaces (newlines kept), so the
 * result has the same length and line structure as the input. A leading BOM
 * becomes a space.
 */
export function blankJsonComments(text: string): string {
  const out = text.split('');
  let i = 0;
  if (out[0] === '﻿') {
    out[0] = ' ';
    i = 1;
  }
  const blank = (from: number, to: number) => {
    for (let k = from; k < to && k < out.length; k++) if (out[k] !== '\n' && out[k] !== '\r') out[k] = ' ';
  };
  let inString = false;
  while (i < text.length) {
    const c = text[i];
    if (inString) {
      if (c === '\\') {
        i += 2;
        continue;
      }
      if (c === '"') inString = false;
      i++;
      continue;
    }
    if (c === '"') {
      inString = true;
      i++;
      continue;
    }
    if (c === '/' && text[i + 1] === '/') {
      const end = text.indexOf('\n', i);
      const stop = end === -1 ? text.length : end;
      blank(i, stop);
      i = stop;
      continue;
    }
    if (c === '/' && text[i + 1] === '*') {
      const end = text.indexOf('*/', i + 2);
      const stop = end === -1 ? text.length : end + 2;
      blank(i, stop);
      i = stop;
      continue;
    }
    if (c === ',') {
      // Trailing comma: the next significant character (comments skipped) closes a container.
      let k = i + 1;
      for (;;) {
        while (k < text.length && /\s/.test(text[k])) k++;
        if (text[k] === '/' && text[k + 1] === '/') {
          const e = text.indexOf('\n', k);
          k = e === -1 ? text.length : e;
          continue;
        }
        if (text[k] === '/' && text[k + 1] === '*') {
          const e = text.indexOf('*/', k + 2);
          k = e === -1 ? text.length : e + 2;
          continue;
        }
        break;
      }
      if (text[k] === ']' || text[k] === '}') out[i] = ' ';
    }
    i++;
  }
  return out.join('');
}

/** Historical name kept for vscode-settings callers: comments + trailing commas removed. */
export function stripJsonComments(text: string): string {
  return blankJsonComments(text);
}

export type JsoncResult<T = unknown> =
  | { ok: true; value: T; hasComments: boolean; bom: boolean }
  | { ok: false; message: string; line?: number; col?: number };

function positionOf(text: string, pos: number): { line: number; col: number } {
  let line = 1;
  let col = 1;
  for (let k = 0; k < pos && k < text.length; k++) {
    if (text[k] === '\n') {
      line++;
      col = 1;
    } else col++;
  }
  return { line, col };
}

/**
 * Parse a JSONC text. Never throws. `hasComments` tells a writer that a plain
 * `JSON.stringify` rewrite would DROP the user's comments.
 */
export function parseJsonc<T = unknown>(text: string, file?: string): JsoncResult<T> {
  const blanked = blankJsonComments(text);
  const bom = text.charCodeAt(0) === 0xfeff;
  const hasComments = blanked.replace(/\s+/g, '') !== text.replace(/^﻿/, '').replace(/\s+/g, '');
  try {
    return { ok: true, value: JSON.parse(blanked) as T, hasComments, bom };
  } catch (err) {
    const raw = err instanceof Error ? err.message : String(err);
    const label = file ? `${file}: ` : '';
    const lc = /line (\d+) column (\d+)/.exec(raw);
    if (lc) return { ok: false, message: `${label}${raw}`, line: Number(lc[1]), col: Number(lc[2]) };
    const at = /position (\d+)/.exec(raw);
    if (at) {
      const { line, col } = positionOf(text, Number(at[1]));
      return { ok: false, message: `${label}${raw} (line ${line}, column ${col})`, line, col };
    }
    return { ok: false, message: `${label}${raw}` };
  }
}
